import React, { createContext, useState, useContext, useEffect } from 'react';
import { firebaseAuth } from '../firebase/auth';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Login function using Firebase
  const login = async (email, password, rememberMe = false) => {
    setError(null);
    try {
      const result = await firebaseAuth.login(email, password);
      
      if (result.success) {
        // If rememberMe is true, store the auth persistence
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        // Get user details
        const user = {
          uid: result.user.uid,
          email: result.user.email,
          name: result.user.displayName || email.split('@')[0],
          emailVerified: result.user.emailVerified
        };
        
        setCurrentUser(user);
        return { success: true, user };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to login. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout function using Firebase
  const logout = async () => {
    setError(null);
    try {
      const result = await firebaseAuth.logout();
      
      if (result.success) {
        setCurrentUser(null);
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to logout. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Registration function using Firebase
  const register = async (email, password, name) => {
    setError(null);
    try {
      const result = await firebaseAuth.register(email, password, name);
      
      if (result.success) {
        // Get user details
        const user = {
          uid: result.user.uid,
          email: result.user.email,
          name: name,
          emailVerified: result.user.emailVerified
        };
        
        setCurrentUser(user);
        return { success: true, user };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to register. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Send password reset email
  const sendPasswordResetEmail = async (email) => {
    setError(null);
    try {
      const result = await firebaseAuth.sendPasswordResetEmail(email);
      
      if (result.success) {
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to send password reset email. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Reset password with code
  const resetPassword = async (code, newPassword) => {
    setError(null);
    try {
      const result = await firebaseAuth.confirmPasswordReset(code, newPassword);
      
      if (result.success) {
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to reset password. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Verify email with code
  const verifyEmail = async (code) => {
    setError(null);
    try {
      const result = await firebaseAuth.verifyEmail(code);
      
      if (result.success) {
        // Update the current user's email verification status
        if (currentUser) {
          setCurrentUser({
            ...currentUser,
            emailVerified: true
          });
        }
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to verify email. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Set up auth state listener on component mount
  useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      if (user) {
        // User is signed in
        const userData = {
          uid: user.uid,
          email: user.email,
          name: user.displayName || user.email.split('@')[0],
          emailVerified: user.emailVerified
        };
        setCurrentUser(userData);
      } else {
        // User is signed out
        setCurrentUser(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Context value
  const value = {
    currentUser,
    login,
    logout,
    register,
    sendPasswordResetEmail,
    resetPassword,
    verifyEmail,
    isAuthenticated: !!currentUser,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
